import client from "./client";
import { formatGpsCoordinates, toGeoJsonPoint } from '../services/gpsCoordinates';
import { publishCollectionChange } from '../utils/collectionSync';
import { getCachedValue, setCachedValue } from './cacheService';
import { markCacheSeeded } from './cacheMeta';
import { NIGERIAN_STATES } from '../component/Admin/data/enums';

const GEO_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const GEO_CACHE_KEYS = {
  states: 'reference:states',
  lgas: (state) => `reference:lgas:${String(state ?? '').trim().toLowerCase()}`,
  wards: (state, lga) => `reference:wards:${String(state ?? '').trim().toLowerCase()}:${String(lga ?? '').trim().toLowerCase()}`,
};

function unwrapPayload(responseData) {
  const envelope = responseData ?? {};
  return envelope.data ?? envelope;
}

function extractItems(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  if (Array.isArray(payload?.data?.results)) return payload.data.results;
  return [];
}

function normalizeTextList(responseData, keys = ['name', 'label', 'value']) {
  const payload = unwrapPayload(responseData);
  const items = extractItems(payload);
  const seen = new Set();

  return items.reduce((list, item) => {
    let text = '';

    if (typeof item === 'string' || typeof item === 'number') {
      text = String(item).trim();
    } else if (item && typeof item === 'object') {
      for (const key of keys) {
        const candidate = String(item?.[key] ?? '').trim();
        if (candidate) {
          text = candidate;
          break;
        }
      }
    }

    if (!text || seen.has(text)) return list;
    seen.add(text);
    list.push(text);
    return list;
  }, []);
}

function readCachedList(cacheKey) {
  const cached = getCachedValue(cacheKey)?.value;
  return Array.isArray(cached)
    ? cached.map((value) => String(value).trim()).filter(Boolean)
    : [];
}

function writeCachedList(cacheKey, items, source) {
  if (!Array.isArray(items) || items.length === 0) return;
  setCachedValue(cacheKey, items, GEO_CACHE_TTL_MS);
  markCacheSeeded({ source });
}

function fallbackStates() {
  return NIGERIAN_STATES.map((item) => item.value);
}

function normalizeGeo(row = {}) {
  return {
    uuid: row.uuid ?? null,
    id:   row.uuid ?? null,
    state:          row.state          ?? null,
    lga:            row.lga            ?? null,
    ward:           row.ward           ?? null,
    ecological_zone:row.ecological_zone ?? null,
    data_status:    row.data_status    ?? 'draft',
    coordinates:    formatGpsCoordinates(row.point_geojson),
    boundary_polygon: row.boundary_polygon_geojson ? 'GeoJSON polygon' : null,
    point_geojson:            row.point_geojson            ?? null,
    boundary_polygon_geojson: row.boundary_polygon_geojson ?? null,
    submitted_by:  row.submitted_by  ?? null,
    submitted_at:  row.submitted_at  ?? null,
    validated_by:  row.validated_by  ?? null,
    validated_at:  row.validated_at  ?? null,
    approved_by:   row.approved_by   ?? null,
    approved_at:   row.approved_at   ?? null,
    rejected_at:   row.rejected_at   ?? null,
    rejection_notes: row.rejection_notes ?? null,
    created_at:    row.created_at    ?? null,
    updated_at:    row.updated_at    ?? null,
  };
}

function extractItem(responseData, targetUuid) {
  const d = responseData?.data ?? responseData ?? {};
  // POST returns a flat data object; GET/{uuid} and PUT return items array
  if (Array.isArray(d.items)) {
    const match = targetUuid ? d.items.find(i => i.uuid === targetUuid) : d.items[0];
    return normalizeGeo(match ?? {});
  }
  return normalizeGeo(d);
}

function buildPayload({ state, lga, ward, ecological_zone, coordinates }) {
  return {
    state:           state           || null,
    lga:             lga             || null,
    ward:            ward            || null,
    ecological_zone: ecological_zone || null,
    point_geojson:   toGeoJsonPoint(coordinates),
  };
}

export const geospatialApi = {
  collectionKey: 'geospatial-references',

  async list(params = {}) {
    const { data } = await client.get('/v1/geospatial-references', {
      params: { per_page: 200, ...params },
    });
    const items      = data?.data?.items ?? [];
    const pagination = data?.data?.pagination ?? null;
    return { items: items.map(normalizeGeo), pagination };
  },

  async states() {
    try {
      const { data } = await client.get('/v1/geospatial-references/states');
      const items = normalizeTextList(data, ['state', 'name', 'label', 'value']);
      if (items.length > 0) {
        writeCachedList(GEO_CACHE_KEYS.states, items, 'geospatial:states');
        return items;
      }

      const cached = readCachedList(GEO_CACHE_KEYS.states);
      if (cached.length > 0) return cached;
      return fallbackStates();
    } catch {
      const cached = readCachedList(GEO_CACHE_KEYS.states);
      if (cached.length > 0) return cached;
      return fallbackStates();
    }
  },

  async lgas(state) {
    if (!state) return [];
    const cacheKey = GEO_CACHE_KEYS.lgas(state);

    try {
      const { data } = await client.get('/v1/geospatial-references/lgas', { params: { state } });
      const items = normalizeTextList(data, ['lga', 'name', 'label', 'value']);
      if (items.length > 0) {
        writeCachedList(cacheKey, items, `geospatial:lgas:${state}`);
        return items;
      }

      const cached = readCachedList(cacheKey);
      if (cached.length > 0) return cached;
      return [];
    } catch {
      const cached = readCachedList(cacheKey);
      if (cached.length > 0) return cached;
      return [];
    }
  },

  async wards(state, lga) {
    if (!state || !lga) return [];
    const cacheKey = GEO_CACHE_KEYS.wards(state, lga);

    try {
      const { data } = await client.get('/v1/geospatial-references/wards', { params: { state, lga } });
      const items = normalizeTextList(data, ['ward', 'name', 'label', 'value']);
      if (items.length > 0) {
        writeCachedList(cacheKey, items, `geospatial:wards:${state}:${lga}`);
        return items;
      }

      const cached = readCachedList(cacheKey);
      if (cached.length > 0) return cached;
      return [];
    } catch {
      const cached = readCachedList(cacheKey);
      if (cached.length > 0) return cached;
      return [];
    }
  },

  async get(uuid) {
    const { data } = await client.get(`/v1/geospatial-references/${uuid}`);
    return extractItem(data, uuid);
  },

  async create(payload) {
    const { data } = await client.post('/v1/geospatial-references', buildPayload(payload));
    const item = extractItem(data);
    publishCollectionChange('geospatial-references', { action: 'create', uuid: item?.uuid ?? null });
    return item;
  },

  async update(uuid, payload) {
    const { data } = await client.put(`/v1/geospatial-references/${uuid}`, buildPayload(payload));
    const item = extractItem(data, uuid);
    publishCollectionChange('geospatial-references', { action: 'update', uuid });
    return item;
  },

  async remove(uuid) {
    const { data } = await client.delete(`/v1/geospatial-references/${uuid}`);
    publishCollectionChange('geospatial-references', { action: 'remove', uuid });
    return data;
  },

  async submit(uuid) {
    const { data } = await client.post(`/v1/geospatial-references/${uuid}/submit`);
    const item = extractItem(data, uuid);
    publishCollectionChange('geospatial-references', { action: 'submit', uuid });
    return item;
  },

  async validate(uuid) {
    const { data } = await client.post(`/v1/geospatial-references/${uuid}/validate`);
    const item = extractItem(data, uuid);
    publishCollectionChange('geospatial-references', { action: 'validate', uuid });
    return item;
  },

  async approve(uuid) {
    const { data } = await client.post(`/v1/geospatial-references/${uuid}/approve`);
    const item = extractItem(data, uuid);
    publishCollectionChange('geospatial-references', { action: 'approve', uuid });
    return item;
  },

  async reject(uuid, notes) {
    const { data } = await client.post(`/v1/geospatial-references/${uuid}/reject`, { rejection_notes: notes });
    const item = extractItem(data, uuid);
    publishCollectionChange('geospatial-references', { action: 'reject', uuid });
    return item;
  },
};
